import createReducer, { PayloadActionType } from 'storeContext/reducers/createReducer'
import {
  SET_IS_LOADING,
  SET_QUERY,
  RESET_QUERY,
  SET_RESET,
  SET_PRODUCTS,
  SET_CURRENT_PRODUCTS,
  SET_CHECKED_ALL,
  SET_CHECKED_ITEM,
  SET_QTY,
  SET_SELECTED_PRODUCT_FOR_FILTER,
} from 'storeContext/actionsType/bomAddProduct'
import type { GetProductListResponseType } from 'features/Product/services/product'

export type BoMProductFilterType = {
  product_name: string
  bom_category: { code: string }
}

export type BoMAddProductApiItemType = BoMProductFilterType & {
  product_id: number
  product_sku_no: string
  product_uom: string
}

export type BoMAddProductItemType = BoMAddProductApiItemType & { qty: number }

export type BoMSelectedComponentType = {
  productId: number
  productName: string
  qty: number
  productUom: string
}

export type BoMAddProductQueryType = {
  pageIndex: number | string
  name?: string
  bomCategory?: string
  productType?: string
  productId?: number | string
}

export type BoMAddProductPaginationType = {
  number_of_elements: number
  page_index: number
  page_size: number
}

export interface BoMAddProductStateType {
  isLoading: boolean
  query: BoMAddProductQueryType
  pagination: BoMAddProductPaginationType
  data: BoMAddProductItemType[]
  tempData: Record<string, BoMAddProductApiItemType>
  dataSelected: Record<string, BoMSelectedComponentType>
  itemQty: Record<string, number | string>
  checkedItems: number[]
  selectedProductForFilter: BoMProductFilterType | null
}

export const initialStateBoMAddProduct: BoMAddProductStateType = {
  isLoading: false,
  query: {
    pageIndex: 1,
  },
  pagination: {
    number_of_elements: 0,
    page_index: 1,
    page_size: 20,
  },
  data: [],
  tempData: {},
  dataSelected: {},
  itemQty: {},
  checkedItems: [],
  selectedProductForFilter: null,
}

const bomAddProductReducer = createReducer<BoMAddProductStateType>({
  [SET_IS_LOADING]: (state, action: PayloadActionType<boolean>) => {
    state.isLoading = action.payload
  },
  [SET_QUERY]: (state, action: PayloadActionType<{ query: Partial<BoMAddProductQueryType> }>) => {
    state.isLoading = true
    state.query = {
      ...state.query,
      ...action.payload.query,
    }
  },
  [RESET_QUERY]: (state) => {
    state.query = {
      pageIndex: 1,
    }
  },
  [SET_PRODUCTS]: (
    state,
    action: PayloadActionType<GetProductListResponseType<BoMAddProductApiItemType>>,
  ) => {
    const { data, pagination } = action.payload
    const ids = Object.keys(state.dataSelected)

    data.forEach((item) => {
      state.tempData[item.product_id] = item

      const productId = item.product_id.toString()
      if (ids.includes(productId)) {
        state.itemQty[productId] = state.dataSelected[productId].qty
      } else {
        delete state.itemQty[productId]
      }
    })

    state.data = data.map((item) => ({ ...item, qty: 0 }))
    state.pagination = {
      number_of_elements: pagination.number_of_elements,
      page_index: pagination.page_index,
      page_size: pagination.page_size,
    }

    state.isLoading = false
  },
  [SET_CURRENT_PRODUCTS]: (
    state,
    action: PayloadActionType<Record<string, BoMSelectedComponentType>>,
  ) => {
    const ids = Object.keys(action.payload)

    state.dataSelected = action.payload
    state.checkedItems = ids.map(Number)
  },
  [SET_CHECKED_ALL]: (state, action: PayloadActionType<boolean>) => {
    const checked = action.payload

    if (checked) {
      state.checkedItems = [
        ...state.checkedItems,
        ...state.data.map(({ product_id }) => product_id),
      ]
    } else {
      state.checkedItems = []
    }
  },
  [SET_CHECKED_ITEM]: (state, action: PayloadActionType<number>) => {
    const id = action.payload
    const isExist = state.checkedItems.some((itemId) => itemId === id)

    if (isExist) {
      state.checkedItems = state.checkedItems.filter((itemId) => itemId !== id)
    } else {
      state.checkedItems = [...state.checkedItems, id]
    }
  },
  [SET_QTY]: (
    state,
    action: PayloadActionType<{ id: number | string; value: number | string }>,
  ) => {
    const { id, value } = action.payload

    state.itemQty[id] = value
  },
  [SET_RESET]: () => initialStateBoMAddProduct,
  [SET_SELECTED_PRODUCT_FOR_FILTER]: (
    state,
    action: PayloadActionType<BoMProductFilterType | null>,
  ) => {
    state.selectedProductForFilter = action.payload
  },
})

export default bomAddProductReducer
